import { combineReducers, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { LocalStorage } from '@vectord/utils';

import { addLeads } from './addLeads';
import { bulkUpdateLeadsSlice } from './bulkUpdateLeads';
import { LOAD_STORED_STATE } from './constants';
import { exportLeadsSlice } from './exportLeads';
import { leadListSlice } from './leadList';
import { secondaryFilterSlice } from './secondaryFilter';
import { selectLeadAttributesModalSlice } from './selectLeadAttributesModal';

// Keys to exclude from localStorage for each slice
const STORAGE_EXCLUDE_KEYS = {
  leadList: ['isAdvancedSearchOpen', 'isEmailOpen', 'page'],
  secondaryFilter: [],
} as const;

// Helper to remove excluded keys from state
const removeExcludedKeys = (state: any, sliceName: keyof typeof STORAGE_EXCLUDE_KEYS) => {
  const excludeKeys = STORAGE_EXCLUDE_KEYS[sliceName] || [];
  const filteredState = { ...state };
  excludeKeys.forEach((key) => {
    delete filteredState[key];
  });
  return filteredState;
};

// Create a slice for managing listId
export const listIdSlice = createSlice({
  name: 'listDetail/listId',
  initialState: null as string | null,
  reducers: {
    setListId: (_, action: PayloadAction<string | null>) => action.payload,
  },
});

export const listDetailReducers = combineReducers({
  listId: listIdSlice.reducer,
  addLeads: addLeads.reducer,
  selectLeadAttributesModal: selectLeadAttributesModalSlice.reducer,
  secondaryFilter: secondaryFilterSlice.reducer,
  leadList: leadListSlice.reducer,
  bulkUpdateLeads: bulkUpdateLeadsSlice.reducer,
  exportLeads: exportLeadsSlice.reducer,
});

// Create a middleware to handle localStorage
export const createListDetailStorageMiddleware = () => (store: any) => (next: any) => (action: any) => {
  const result = next(action);

  // Handle listId changes
  if (action.type === listIdSlice.actions.setListId.type) {
    const listId = action.payload;
    if (listId) {
      const storageInstance = LocalStorage.getInstance('store.listDetail');
      const storedConfigs = storageInstance?.config || {};
      const storedState = storedConfigs[listId];

      if (storedState) {
        // Dispatch stored state to all slices
        store.dispatch({
          type: LOAD_STORED_STATE,
          payload: storedState,
        });
      }
    }
  }

  // Handle saving state changes
  const { listId } = store.getState().listDetail;
  if (listId && action.type.startsWith('listDetail/')) {
    const state = store.getState().listDetail;
    const storageInstance = LocalStorage.getInstance('store.listDetail');
    if (storageInstance) {
      const { listId: omittedId, ...stateToStore } = state;

      // Filter out excluded keys for each slice before storing
      const filteredState = {
        leadList: removeExcludedKeys(stateToStore.leadList, 'leadList'),
        secondaryFilter: removeExcludedKeys(stateToStore.secondaryFilter, 'secondaryFilter'),
      };

      // Get existing configs and update the specific listId
      const existingConfigs = storageInstance.config || {};
      storageInstance.config = {
        ...existingConfigs,
        [listId]: filteredState,
      };
    }
  }

  return result;
};

export const { setListId } = listIdSlice.actions;
